import { Theme } from "@mui/material";
import type { CSSProperties } from "react";
import { defaultStyles } from "@visx/tooltip";

/**
 * Theme-aware tooltip container styles shared by the visx-based chart
 * components (bar, pie, stacked-bar, multi-line, heatmap). These charts sit
 * outside the DOM tree that reads `sx`, so each chart calls `useTheme()` and
 * passes the theme in here instead of hardcoding a light-mode-only white box.
 */
export const getChartTooltipStyles = (theme: Theme) => ({
  ...defaultStyles,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "4px",
  padding: "8px 12px",
  boxShadow: theme.shadows[3],
});

/**
 * Theme-aware legend box styles shared by stacked-bar-chart and
 * multi-line-chart (previously a hardcoded `#e0e0e0`/`#fafafa` light-mode
 * treatment).
 */
export const getChartLegendBoxStyles = (theme: Theme) => ({
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor:
    theme.palette.mode === "dark"
      ? theme.palette.background.paper
      : theme.palette.grey[50],
});

/**
 * Shared color-swatch + label row used inside every visx chart's hover
 * tooltip (bar/pie/stacked-bar/multi-line) — previously the identical
 * `{ display: "flex", alignItems: "center", gap: "6px" }` object was
 * retyped in all four files.
 */
export const chartTooltipRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
};

/** Small colored square shown next to a tooltip row's label/value. */
export const getChartTooltipSwatchStyle = (color: string): CSSProperties => ({
  width: "10px",
  height: "10px",
  backgroundColor: color,
  borderRadius: "2px",
  flexShrink: 0,
});
